'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { X, Cookie, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase-browser';
import { usePathname } from 'next/navigation';
import { getLocaleFromPathname } from '@/lib/i18n';
import { localePath } from '@/lib/links';

export default function CookieBanner() {
  const locale = getLocaleFromPathname(usePathname() || '');
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    // Check if user has already accepted/rejected
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Delay showing the banner for better UX (4-8 seconds)
      const timer = setTimeout(() => {
        setShouldRender(true);
        // Trigger the transition in the next tick
        setTimeout(() => setIsVisible(true), 50);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Generate or retrieve anonymous_id from localStorage
  const getAnonymousId = (): string => {
    const key = 'gt_anon_id';
    let id = localStorage.getItem(key);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(key, id);
    }
    return id;
  };

  // Log consent to Supabase via API route
  const logConsent = async (choice: 'accepted' | 'rejected') => {
    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();

      await fetch('/api/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          anonymous_id: getAnonymousId(),
          choice,
          page_url: window.location.href,
          user_id: session?.user?.id ?? null,
        })
      });
    } catch {
      console.warn('Consent logging failed silently');
    }
  };

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // GA Consent Mode v2 — grant analytics on user accept
      (window as any).gtag('consent', 'update', {
        analytics_storage: 'granted',
        functionality_storage: 'granted',
        personalization_storage: 'granted',
      });
    }
    void logConsent('accepted');
    setIsVisible(false);
    setTimeout(() => setShouldRender(false), 500);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // GA Consent Mode v2 — keep denied on decline
      (window as any).gtag('consent', 'update', {
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
      });
    }
    void logConsent('rejected');
    setIsVisible(false);
    setTimeout(() => setShouldRender(false), 500);
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('This browser does not support notifications');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success('Notifications enabled!');
      } else if (permission === 'denied') {
        toast.error('Notifications blocked by browser settings');
      }
    } catch (error) {
      console.error('Notification error:', error);
    }
  };

  if (!shouldRender) return null;

  return (
    <div
      className={`fixed bottom-6 left-6 z-50 p-4 sm:p-0 transition-all duration-500 ease-out transform ${
        isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-12 opacity-0 scale-95 pointer-events-none'
      }`}
    >
      <div className="glass-card rounded-[2rem] shadow-2xl p-7 max-w-[22rem] w-full relative overflow-hidden ring-1 ring-white/5">
        {/* Decorative gradient glow */}
        <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-primary/20 blur-[50px] pointer-events-none" />
        
        <button 
          onClick={handleDecline}
          className="absolute top-5 right-5 text-muted-foreground hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-5 mb-8">
          <div className="p-3.5 bg-primary/15 rounded-2xl shrink-0 shadow-inner">
            <Cookie className="w-7 h-7 text-primary" />
          </div>
          <div className="space-y-1">
            <h3 className="font-black text-xl tracking-tighter text-foreground uppercase">Cookies</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              We use cookies to refine your experience and analyze directory traffic. <Link href={localePath(locale, '/cookies')} prefetch={false} className="text-primary hover:underline">Learn more</Link>
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex gap-3">
            <Button variant="ghost" className="flex-1 h-12 rounded-xl text-muted-foreground hover:text-white hover:bg-white/5 border border-white/5" onClick={handleDecline}>
              Decline
            </Button>
            <Button className="flex-1 h-12 rounded-xl bg-primary hover:bg-link-hover text-white font-bold shadow-lg shadow-primary/20 transition-all hover:scale-[1.02]" onClick={handleAccept}>
              Accept
            </Button>
          </div>
          
          <div className="relative pt-6 mt-2 border-t border-white/5">
            <div className="flex items-center gap-2 mb-3 justify-center">
               <Bell className="w-3 h-3 text-primary/70" />
               <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/70">Stay Updated</p>
            </div>
            <Button 
              variant="secondary" 
              size="sm" 
              className="w-full h-11 rounded-xl bg-white/5 hover:bg-white/10 text-foreground border border-white/5 text-xs transition-all"
              onClick={requestNotificationPermission}
            >
              Enable Notifications
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
